import {
  Box3,
  Group,
  Plane,
  Quaternion,
  Vector2,
  Vector3,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  ShaderMaterial,
  Color,
  Box3Helper,
} from "three";
import {
  ICameraBehavior,
  pageCoordToNormalizedDeviceCoord,
  TControlMode,
  TProjectionMode,
} from "../utils/camera-utils";
import { InputHandler } from "../utils/input-handler/input-handler";
import { Viewer } from "../viewer";
import { OrthographicOrbitBehavior } from "./Behavior/OrthographicOrbitBehavior";
import { OrthographicTrackballBehavior } from "./Behavior/OrthographicTrackballBehavior";
import { PerspectiveOrbitBehavior } from "./Behavior/PerspectiveOrbitBehavior";
import { PerspectiveTrackballBehavior } from "./Behavior/PerspectiveTrackballBehavior";
import { IQuick3DCamera } from "./quick3d-camera-interface";
import {
  IOrthographicQuick3DCameraArguments,
  Quick3DOrthographicCamera,
} from "./quick3d-orthographic-camera";
import {
  IQuick3DPerspectiveCameraArguments,
  Quick3DPerspectiveCamera,
} from "./quick3d-perspective-camera";
import { PerspectiveCustomBehavior } from "./Behavior/PerspectiveCustomBehavior";
import { OrthographicCustomBehavior } from "./Behavior/OrthographicCustomBehavior";
import { SCENE_TYPE } from "../config/ViewerConfig";

export class CameraManager {
  private _viewer: Viewer;

  // manager
  private _controlMap: Map<string, ICameraBehavior>;
  private _targetObject: Group;
  private _cameraPlane: Plane;

  // current
  private _currentCamera: IQuick3DCamera;
  private _currentControlMode: TControlMode;
  private _currentProjectionMode: TProjectionMode;

  // camera
  private _orthoCamera: Quick3DOrthographicCamera;
  private _perspCamera: Quick3DPerspectiveCamera;
  private _isInitOrtho: boolean;

  // controller
  private _worldOrientation: Quaternion;
  private _rotatePoint: Vector3;
  private _rotateSphereGeometry: SphereGeometry;
  // private _rotateSphereMaterial: MeshBasicMaterial;
  private _rotateSphereMaterial: ShaderMaterial;
  private _rotateSphere: Mesh;

  // helper
  private _bboxHelper: Box3Helper;

  // dubug
  private _isRotateDebug: boolean;

  private _zoomRangePermittable: {
    minWidth: number;
    minHeight: number;
    maxWidth: number;
    maxHeight: number;
  };
  private _enablePan: boolean;
  private _enableRotate: boolean;
  private _enableZoom: boolean;

  // setting
  private _defaultControlMode: TControlMode;
  private _defaultProjectionMode: TProjectionMode;

  public get targetObject(): Group {
    return this._targetObject;
  }

  public get camera(): IQuick3DCamera {
    return this._currentCamera;
  }

  public get currentControlMode(): TControlMode {
    return this._currentControlMode;
  }

  public get perspectiveCamera(): Quick3DPerspectiveCamera {
    return this._perspCamera;
  }

  public get orthographicCamera(): Quick3DOrthographicCamera {
    return this._orthoCamera;
  }

  constructor(
    worldOrientation: Quaternion,
    inputHandler: InputHandler,
    viewer: Viewer
  ) {
    this._viewer = viewer;
    this._currentCamera = undefined as unknown as IQuick3DCamera;
    this._currentControlMode = undefined as unknown as TControlMode;
    this._currentProjectionMode = undefined as unknown as TProjectionMode;

    this._worldOrientation = worldOrientation.clone();

    this._orthoCamera = new Quick3DOrthographicCamera({
      near: 0.1,
      far: 10000,
      viewSize: 300,
      aspect: 1,
    } as IOrthographicQuick3DCameraArguments);

    this._perspCamera = new Quick3DPerspectiveCamera({
      near: 0.1,
      far: 10000,
      fieldOfView: 50,
      aspect: 1,
    } as IQuick3DPerspectiveCameraArguments);

    this._targetObject = new Group();

    this._defaultControlMode = "custom";
    this._defaultProjectionMode = "perspective";

    this._cameraPlane = new Plane().setFromNormalAndCoplanarPoint(
      this._perspCamera.getWorldDirection(new Vector3()),
      this._perspCamera.getWorldPosition(new Vector3())
    );

    this._controlMap = new Map();
    this._controlMap.set(
      "perspectiveorbit",
      new PerspectiveOrbitBehavior(
        this._perspCamera,
        this._targetObject,
        this._worldOrientation
      )
    );
    this._controlMap.set(
      "orthographicorbit",
      new OrthographicOrbitBehavior(
        this._orthoCamera,
        this._targetObject,
        this._worldOrientation
      )
    );
    this._controlMap.set(
      "perspectivetrackball",
      new PerspectiveTrackballBehavior(
        this._perspCamera,
        this._targetObject,
        this._worldOrientation,
        this._viewer
      )
    );
    this._controlMap.set(
      "orthographictrackball",
      new OrthographicTrackballBehavior(
        this._orthoCamera,
        this._targetObject,
        this._worldOrientation,
        this._viewer
      )
    );
    this._controlMap.set(
      "perspectivecustom",
      new PerspectiveCustomBehavior(
        this._perspCamera,
        this._targetObject,
        this._worldOrientation,
        this._viewer
      )
    );
    this._controlMap.set(
      "orthographiccustom",
      new OrthographicCustomBehavior(
        this._orthoCamera,
        this._targetObject,
        this._worldOrientation,
        this._viewer
      )
    );
    this._rotatePoint = new Vector3();
    // this._rotateSphereGeometry = new SphereGeometry(0.2, 32, 16);
    // this._rotateSphereMaterial = new MeshBasicMaterial({ color: 0xff0000 });
    // this._rotateSphere = new Mesh(
    //   this._rotateSphereGeometry,
    //   this._rotateSphereMaterial
    // );

    this.resetControlScheme();
    this.resetCameraTransforms();
    this._zoomRangePermittable = {
      minWidth: Number.MIN_SAFE_INTEGER,
      minHeight: Number.MIN_SAFE_INTEGER,
      maxWidth: Number.MAX_SAFE_INTEGER,
      maxHeight: Number.MAX_SAFE_INTEGER,
    };
    this._isInitOrtho = true;
    this._enablePan = true;
    this._enableRotate = true;
    this._enableZoom = true;

    this._rotateSphereGeometry = new SphereGeometry(5, 32, 16);
    this._rotateSphereMaterial = new ShaderMaterial({
      uniforms: {
        color1: {
          value: new Color("gray"),
        },
        color2: {
          value: new Color("white"),
        },
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color1;
        uniform vec3 color2;

        varying vec2 vUv;

        void main() {

          gl_FragColor = vec4(mix(color1, color2, vUv.y), 1.0);
        }
      `,
      wireframe: true,
    });
    this._rotateSphereMaterial.depthTest = false;

    this._rotateSphere = new Mesh(
      this._rotateSphereGeometry,
      this._rotateSphereMaterial
    );

    this._isRotateDebug = false;
  }

  public getControl(): ICameraBehavior {
    return this._controlMap.get(
      this._currentProjectionMode + this.currentControlMode
    ) as ICameraBehavior;
  }

  public getProjectionMode(): TProjectionMode {
    return this._currentProjectionMode;
  }

  public setProjectionMode(mode: TProjectionMode): void {
    if (this._currentProjectionMode === mode) {
      return;
    }
    this._currentProjectionMode = mode;
    switch (mode) {
      case "orthographic":
        this._currentCamera = this._orthoCamera;
        if (this._isInitOrtho) {
          this._viewer.fit();
          this._isInitOrtho = false;
        }
        break;
      case "perspective":
        this._currentCamera = this._perspCamera;
        break;
      default:
        console.error(`unexpected projection mode '${mode}'`);
    }
    this._viewer.updateRenderer(1);
  }

  public setControlMode(mode: TControlMode, bbox: Box3): void {
    if (mode === this._currentControlMode) {
      return;
    }

    this._currentControlMode = mode;
    this._viewer.updateRenderer(1);
  }

  public resetCameraTransforms(): void {
    this._targetObject.position.set(0, 0, 0);
    this._targetObject.quaternion.copy(this._worldOrientation);
    this._targetObject.scale.set(1, 1, 1);
    this._targetObject.up.set(0, 1, 0).applyQuaternion(this._worldOrientation);

    this._orthoCamera.position.set(-100, -100, 100);
    this._orthoCamera.up.set(0, 0, 1);
    this._orthoCamera.lookAt(0, 0, 0);

    this._perspCamera.position.set(-100, -100, 100);
    this._perspCamera.up.set(0, 0, 1);
    this._perspCamera.lookAt(0, 0, 0);

    this._targetObject.updateMatrixWorld(true);

    this._rotatePoint.set(0, 0, 0);
  }

  public resetCurrentCameraTransforms(): void {
    this._targetObject.position.set(0, 0, 0);
    this._targetObject.quaternion.copy(this._worldOrientation);
    this._targetObject.scale.set(1, 1, 1);
    this._targetObject.up.set(0, 1, 0).applyQuaternion(this._worldOrientation);

    this._currentCamera.position.set(-100, -100, 100);
    this._currentCamera.up.set(0, 0, 1);
    this._currentCamera.lookAt(0, 0, 0);
    this._currentCamera.updateMatrixWorld(true);
  }

  // This function reset the control mode to orbit mode and  camera projection mode to perspective mode
  public resetControlScheme(): void {
    this._currentControlMode = this._defaultControlMode;
    this._currentProjectionMode = this._defaultProjectionMode;
    this._currentCamera = this._perspCamera;
  }

  public setAspect(aspect: number): void {
    this._perspCamera.aspect = aspect;
    this._perspCamera.updateProjectionMatrix();
    this._orthoCamera.aspect = aspect;
    this._orthoCamera.updateProjectionMatrix();
  }

  public cameraStart(payLoad: { pageX: number; pageY: number }): void {
    const ndc = this.getNDC(payLoad.pageX, payLoad.pageY, new Vector2());
    this.getControl().start(ndc.x, ndc.y);
  }

  public rotateStart(payLoad: { pageX: number; pageY: number }): void {
    // zoom制限: 回転
    this._enableZoom = false;
    const ndc = this.getNDC(payLoad.pageX, payLoad.pageY, new Vector2());
    const center = this.getControl().setRotateCenter(ndc.x, ndc.y);
    this._rotateSphere.position.copy(center || new Vector3(0, 0, 0));

    this._viewer.getScene(SCENE_TYPE.FOREGROUND).add(this._rotateSphere);

    if (this._isRotateDebug) {
      if (this.getControl().addHelper) {
        this.getControl().addHelper();
      }

      this._bboxHelper = new Box3Helper(
        this._viewer._pointCloudManager.bvh.rootTile.boundingBox,
        new Color(0, 1, 0)
      );
      this._viewer.getScene(SCENE_TYPE.FOREGROUND).add(this._bboxHelper);
    }
    this.attenuateRotateSphere();
    this._viewer.updateRenderer(1);
  }

  public cameraEnd(): void {
    this._viewer.debouncedOnCameraChange(1000);
  }

  public rotateEnd(): void {
    // zoom制限: 回転
    this._enableZoom = true;
    this._viewer.getScene(SCENE_TYPE.FOREGROUND).remove(this._rotateSphere);
    this._viewer.updateRenderer(1);
  }

  public cameraPan(payLoad: { pageX: number; pageY: number }): void {
    if (!this._enablePan) return;
    const ndc = this.getNDC(payLoad.pageX, payLoad.pageY, new Vector2());
    this.getControl().pan(ndc.x, ndc.y);

    // 画面外にドラッグした際のカメラ更新
    this._viewer.debouncedOnCameraChange(3000);
    this._viewer.updateRenderer(1);
  }

  public cameraRotate(payLoad: { pageX: number; pageY: number }) {
    if (!this._enableRotate) return;

    this.setSphereVisibility(true);

    if (this._isRotateDebug) {
      if (this.getControl().setHelperVisibility) {
        this.getControl().setHelperVisibility(true);
      }

      this.setBboxHelperVisibility(true);
    }

    const control = this.getControl();
    const ndc = this.getNDC(payLoad.pageX, payLoad.pageY, new Vector2());
    if (control) {
      control.rotate(ndc.x, ndc.y);
      if (this._currentProjectionMode === "perspective") {
        this._cameraPlane.setFromNormalAndCoplanarPoint(
          this.perspectiveCamera.getWorldDirection(new Vector3()),
          this.perspectiveCamera.getWorldPosition(new Vector3())
        );
      }
    }

    // 画面外にドラッグした際のカメラ更新
    this._viewer.debouncedOnCameraChange(1000);
    this.attenuateRotateSphere();
    this._viewer.updateRenderer(1);
  }

  public cameraZoom(payLoad: { pageX: number; pageY: number }) {
    if (!this._enableZoom) return;
    const dir = this.getControl().getZoomDirection(
      payLoad.pageX,
      payLoad.pageY
    );
    this.getControl().flatZoom(dir < 0 ? -1 : 1);
    this.getControl().setDeltaNDC(new Vector2(payLoad.pageX, payLoad.pageY));
    this.attenuateRotateSphere();
    this._viewer.updateRenderer(1);
  }

  public pinchZoom(delta: 1 | -1, sceneBoundingBox: Box3): void {
    this.wheelZoom(delta, sceneBoundingBox);
  }

  public wheelZoom(delta: 1 | -1, sceneBoundingBox: Box3): void {
    if (!this._enableZoom) return;
    if (this._currentProjectionMode === "orthographic") {
      //zooming In
      const camWidth = this._orthoCamera.width / this._orthoCamera.zoom;
      let canZoomIn = true;
      canZoomIn = camWidth > this._zoomRangePermittable.minWidth;

      //zooming Out
      let canZoomOut = true;
      canZoomOut = camWidth < this._zoomRangePermittable.maxWidth;

      if ((delta === -1 && !canZoomIn) || (delta === 1 && !canZoomOut)) {
        return;
      }
    }

    const control = this.getControl();
    if (control) {
      if (this._currentProjectionMode === "perspective") {
        control.zoomSpeed =
          0.4 * this.calculatePerspectiveZoomFactor(sceneBoundingBox);
      }
      control.wheel(0, 0, delta);
      if (this._currentProjectionMode === "perspective") {
        this._cameraPlane.setFromNormalAndCoplanarPoint(
          this.perspectiveCamera.getWorldDirection(new Vector3()),
          this.perspectiveCamera.getWorldPosition(new Vector3())
        );
      }
    }

    this.attenuateRotateSphere();

    // 画面外にドラッグした際のカメラ更新
    this._viewer.debouncedOnCameraChange(1000);
    this._viewer.updateRenderer(1);
  }

  private calculatePerspectiveZoomFactor(box: Box3): number {
    const center = box.getCenter(new Vector3());
    const dist = this._perspCamera
      .getWorldPosition(new Vector3())
      .sub(center)
      .length();
    const size = box.getSize(new Vector3()).length();
    const ratio = dist / size;
    return ratio >= 0.75 ? 2 : 0.5;
  }

  private getNDC(pageX: number, pageY: number, target: Vector2): Vector2 {
    return pageCoordToNormalizedDeviceCoord(
      { pageX, pageY },
      this._viewer.getBoundingRect(),
      target
    );
  }

  public enableControl(controls: {
    pan?: boolean;
    rotate?: boolean;
    dolly?: boolean;
  }): void {
    this._enablePan =
      controls.pan !== undefined ? controls.pan : this._enablePan;
    this._enableRotate =
      controls.rotate !== undefined ? controls.rotate : this._enableRotate;
    this._enableZoom =
      controls.dolly !== undefined ? controls.dolly : this._enableZoom;
  }

  public attenuateRotateSphere() {
    let distance: number;
    const sphereScaleSizeConstant: number = 300;
    if (this._currentCamera === this.orthographicCamera) {
      // 目測による定数
      distance = 370;
    } else {
      distance = this._currentCamera.position.distanceTo(
        this._rotateSphere.position
      );
    }

    this._rotateSphere.scale.set(
      distance / (sphereScaleSizeConstant * this._currentCamera.zoom),
      distance / (sphereScaleSizeConstant * this._currentCamera.zoom),
      distance / (sphereScaleSizeConstant * this._currentCamera.zoom)
    );
  }

  public setSphereVisibility(visible: boolean) {
    visible
      ? this._viewer.getScene(SCENE_TYPE.FOREGROUND)?.add(this._rotateSphere)
      : this._viewer
          .getScene(SCENE_TYPE.FOREGROUND)
          ?.remove(this._rotateSphere);
  }

  public setBboxHelperVisibility(visible: boolean) {
    visible
      ? this._viewer.getScene(SCENE_TYPE.FOREGROUND)?.add(this._bboxHelper)
      : this._viewer.getScene(SCENE_TYPE.FOREGROUND)?.remove(this._bboxHelper);
  }

  public toggleIsRotateDebug() {
    this._isRotateDebug = !this._isRotateDebug;
  }
}
